import { ApplicationConfig, ErrorHandler, importProvidersFrom, InjectionToken, provideAppInitializer } from '@angular/core';
import { provideGlobalApiConfiguration } from '../global-api-configuration';
import { ContractDataCacheModule, TimeSeriesCacheModule } from 'core';
import { routes, routingConfiguration } from './sphere-app.routes';
import { MatNativeDateModule } from '@angular/material/core';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { MonacoEditorModule, NGX_MONACO_EDITOR_CONFIG } from 'ngx-monaco-editor-v2';
import { NavigationModule } from './portal/navigation/navigation.module';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { provideState, provideStore } from '@ngrx/store';
import { environment } from '@env/environment';

import {
    CurveSymbolEffects,
    curveSymbolFeature,
    SymbolEffects,
    symbolFeature,
    UnitCurrencyEffects,
    unitCurrencyFeature,
    webSocketSymbolsFeatureKey,
    webSocketSymbolsReducer,
    WsSymbolEffects,
    getMetaReducers
} from 'core/+state';
import { FullRouterStateSerializer, provideRouterStore, routerReducer } from '@ngrx/router-store';
import { xAppInterceptor } from './portal/interceptors/x-app.interceptor';
import { servicesUnavailableInterceptor } from './portal/redirection/services-unavailable/services-unavailable.interceptor';
import { MAT_CHIPS_DEFAULT_OPTIONS } from '@angular/material/chips';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideEffects } from '@ngrx/effects';
import { EntityDataModule } from '@ngrx/data';
import { ServiceWorkerModule } from '@angular/service-worker';
import { ReactiveFormsModule } from '@angular/forms';
import { FormlyModule } from '@ngx-formly/core';
import { formlyTypes } from 'shared/formly/formly-type-map';
import { FormlyMaterialModule } from '@ngx-formly/material';
import { MtxNativeDatetimeModule } from '@ng-matero/extensions/core';
import { MtxDatetimepickerModule } from '@ng-matero/extensions/datetimepicker';
import { SphereMonacoModule } from 'shared/sphere-monaco/sphere-monaco.module';
import { MatToolbarModule } from '@angular/material/toolbar';
import { AngularSplitModule } from 'angular-split';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { SphereMonacoService } from 'shared/sphere-monaco/sphere-monaco.service';
import { bearerTokenInterceptor } from './portal/dronaHttpInterceptor';
import { GryphonAgGridModule } from 'core/ag-grid/gryphon-ag-grid.module';
import { SettingsFullscreenDialogModule } from '../../../../libs/platform/src/lib/settings/components/settings-fullscreen-dialog/settings-fullscreen-dialog.module';
import { CompanySettingsModule } from '../../../../libs/platform/src/lib/settings/company-settings/company-settings.module';
import { provideRouter, withRouterConfig } from '@angular/router';
import { maxValidationMessage, minValidationMessage } from 'core/utils/validation-messages';
import { provideHighcharts } from 'highcharts-angular';
import {
    AppConfiguration,
    GlobalErrorHandler,
    NotificationModule,
    SecurityModule,
    SPHERE_ENVIRONMENT_PRODUCTION,
    SystemSettingsModule,
    UserSettingsModule
} from '@sphere/platform';
import { NotificationSubscriptionsModule } from 'built-in-apps-shared';
import { provideAuth0 } from '@auth0/auth0-angular';
import { sphereInitializer } from './init-utils/sphere-initializer';

export const DYNAMIC_SPHERE_CONFIG = new InjectionToken<AppConfiguration>('DYNAMIC_SPHERE_CONFIG');

export function createSphereAppConfig(dynamicConfig: AppConfiguration): ApplicationConfig {
    return {
        providers: [
            { provide: DYNAMIC_SPHERE_CONFIG, useValue: dynamicConfig },
            { provide: SPHERE_ENVIRONMENT_PRODUCTION, useValue: environment.production },
            /* Configuration and init */
            provideAppInitializer(sphereInitializer()),
            ...provideGlobalApiConfiguration(),

            /* Core framework */
            provideAnimationsAsync(),
            provideRouter(routes, withRouterConfig(routingConfiguration)),

            /* Security */
            provideAuth0({
                ...dynamicConfig.auth0,
                authorizationParams: {
                    redirect_uri: window.location.origin
                }
            }),

            /* State / NGRX */
            provideStore(
                {
                    routerReducer: routerReducer
                },
                {
                    metaReducers: getMetaReducers(environment.production),
                    runtimeChecks: {
                        strictActionImmutability: false,
                        strictActionSerializability: false,
                        strictActionWithinNgZone: false,
                        strictStateImmutability: false,
                        strictStateSerializability: false
                    }
                }
            ),
            provideRouterStore({
                stateKey: 'routerReducer',
                serializer: FullRouterStateSerializer
            }),
            provideStoreDevtools({ maxAge: 25, logOnly: environment.production, connectInZone: true }),
            provideEffects([SymbolEffects, CurveSymbolEffects, WsSymbolEffects, UnitCurrencyEffects]),
            provideState(webSocketSymbolsFeatureKey, webSocketSymbolsReducer),
            provideState(symbolFeature),
            provideState(curveSymbolFeature),
            provideState(unitCurrencyFeature),

            /* Features */
            provideHighcharts({
                options: {
                    lang: {
                        decimalPoint: '.',
                        thousandsSep: ','
                    },
                    chart: {
                        style: {
                            fontFamily: 'Roboto'
                        }
                    },
                    credits: {
                        enabled: false
                    },
                    stockTools: {
                        gui: {
                            enabled: false
                        }
                    }
                },
                // Include Highcharts additional modules (e.g., exporting, accessibility) or custom themes
                modules: () => {
                    return [
                        import('highcharts/esm/highstock'),
                        import('highcharts/esm/highcharts-more'),
                        import('highcharts/esm/modules/solid-gauge'),
                        import('highcharts/esm/themes/dark-unica'),
                        import('highcharts/esm/modules/no-data-to-display'),
                        import('highcharts/esm/indicators/indicators'),
                        import('highcharts/esm/indicators/indicators-all'),
                        import('highcharts/esm/modules/drag-panes'),
                        import('highcharts/esm/modules/annotations'),
                        import('highcharts/esm/modules/annotations-advanced'),
                        import('highcharts/esm/modules/price-indicator'),
                        import('highcharts/esm/modules/stock-tools'),
                        import('highcharts/esm/modules/full-screen'),
                        import('highcharts/esm/modules/accessibility'),
                        import('highcharts/esm/modules/stock')
                    ];
                }
            }),

            /* Legacy modules */
            importProvidersFrom(
                SecurityModule,
                MatNativeDateModule,
                MatMomentDateModule,
                MonacoEditorModule.forRoot(),
                NavigationModule.forRoot(),
                UserSettingsModule,
                GryphonAgGridModule,
                SettingsFullscreenDialogModule,
                NotificationModule,
                TimeSeriesCacheModule,
                ContractDataCacheModule,

                // TODO: Should refactor to provideEntityData(), but needs additional refactoring in administration and data management.
                EntityDataModule.forRoot({}),

                // ServiceWorker
                ServiceWorkerModule.register('/ngsw-worker.js', { enabled: environment.production }),
                ReactiveFormsModule,
                FormlyModule.forRoot({
                    extras: { lazyRender: true },
                    validationMessages: [
                        { name: 'required', message: 'This field is required' },
                        { name: 'min', message: minValidationMessage },
                        { name: 'max', message: maxValidationMessage }
                    ],
                    types: formlyTypes
                }),
                FormlyMaterialModule,
                NotificationSubscriptionsModule,
                CompanySettingsModule,
                MtxNativeDatetimeModule,
                MtxDatetimepickerModule,
                SystemSettingsModule,
                SphereMonacoModule,
                MatToolbarModule,
                AngularSplitModule
            ),

            /* Default value providers */
            { provide: MAT_CHIPS_DEFAULT_OPTIONS, useValue: { appearance: 'fill' } },
            {
                provide: NGX_MONACO_EDITOR_CONFIG,
                useFactory: (sphereMonaco: SphereMonacoService) => {
                    return {
                        onMonacoLoad: () => {
                            // This service instance controls the global editor configuration requirements
                            sphereMonaco.init();
                        }
                    };
                },
                deps: [SphereMonacoService]
            },
            { provide: ErrorHandler, useClass: GlobalErrorHandler },

            /* HTTP client and interceptors */
            // -> Imported after router as to avoid redirecting plugin routes to 404
            provideHttpClient(withInterceptorsFromDi(), withInterceptors([servicesUnavailableInterceptor, bearerTokenInterceptor, xAppInterceptor]))
        ]
    };
}
